import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { LoginResponse } from '@/features/auth/types/auth';

export type AuthSessionPayload = LoginResponse & {
  rememberMe?: boolean;
};

export type AuthState = {
  userId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAtUtc: string | null;
  userName: string | null;
  userEmail: string | null;
  role: string | null;
  permissions: string[];
  clinicId: string | null;
  isAuthenticated: boolean;
  rememberMe: boolean;
};

export const initialAuthState: AuthState = {
  userId: null,
  accessToken: null,
  refreshToken: null,
  expiresAtUtc: null,
  userName: null,
  userEmail: null,
  role: null,
  permissions: [],
  clinicId: null,
  isAuthenticated: false,
  rememberMe: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<AuthSessionPayload>) => {
      state.userId = action.payload.user.userId;
      state.accessToken = action.payload.tokens.accessToken;
      state.refreshToken = action.payload.tokens.refreshToken;
      state.expiresAtUtc = action.payload.tokens.expiresAtUtc;
      state.userName = action.payload.user.displayName;
      state.userEmail = action.payload.user.email ?? null;
      state.role = action.payload.user.role;
      state.permissions = action.payload.user.permissions;
      state.clinicId = action.payload.user.clinicId;
      state.isAuthenticated = true;
      state.rememberMe = action.payload.rememberMe ?? state.rememberMe;
    },
    logout: state => {
      state.userId = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.expiresAtUtc = null;
      state.userName = null;
      state.userEmail = null;
      state.role = null;
      state.permissions = [];
      state.clinicId = null;
      state.isAuthenticated = false;
      state.rememberMe = false;
    },
    profileUpdated: (state, action: PayloadAction<Pick<LoginResponse['user'], 'displayName' | 'email'>>) => {
      state.userName = action.payload.displayName;
      state.userEmail = action.payload.email ?? null;
    }
  }
});

export const { loginSuccess, logout, profileUpdated } = authSlice.actions;
export default authSlice.reducer;
